import React, { useState } from 'react';
import '../../styles/precision-enhancements.css';

/**
 * ErrorLogPanel Component
 * Displays unverified targets and verification issues from Precision Analysis
 * Collapsible panel for transparency about what couldn't be verified
 */
const ErrorLogPanel = ({ errorLog }) => {
  const [showErrorLog, setShowErrorLog] = useState(false);

  if (!errorLog) return null;

  const unverifiedCount = errorLog.unverified_targets?.length || 0;
  const discrepanciesCount = errorLog.discrepancies?.length || 0;
  const limitationsCount = errorLog.vi_limitations?.length || 0;

  const totalIssues = unverifiedCount + discrepanciesCount + limitationsCount;

  if (totalIssues === 0) return null;

  return (
    <div className="error-log-panel">
      <button 
        className="error-log-toggle"
        onClick={() => setShowErrorLog(!showErrorLog)}
        aria-expanded={showErrorLog}
      >
        <span className="toggle-icon">{showErrorLog ? '▼' : '▶'}</span>
        <span className="toggle-text">
          Verification Issues ({totalIssues} {totalIssues === 1 ? 'item' : 'items'})
        </span>
      </button>
      
      {showErrorLog && (
        <div className="error-log-content">
          {/* Unverified Targets */}
          {errorLog.unverified_targets && errorLog.unverified_targets.map((target, idx) => (
            <div key={`unverified-${idx}`} className="error-entry unverified">
              <div className="error-header">
                <span className="error-icon">⚠️</span>
                <span className="error-title">Unverified Target</span>
              </div>
              <div className="error-details">
                <p><strong>Description:</strong> {target.gemini_description}</p>
                <p><strong>Reason:</strong> {target.failure_reason || 'Unknown'}</p>
                {target.attempted_anchors && target.attempted_anchors.length > 0 && (
                  <p><strong>Attempted Anchors:</strong> {target.attempted_anchors.join(', ')}</p>
                )}
                {target.notes && <p><strong>Notes:</strong> {target.notes}</p>}
                {target.timestamp && (
                  <p><strong>Detected At:</strong> {new Date(target.timestamp).toLocaleString()}</p>
                )}
              </div>
            </div>
          ))}
          
          {/* Discrepancies */}
          {errorLog.discrepancies && errorLog.discrepancies.map((disc, idx) => (
            <div key={`discrepancy-${idx}`} className="error-entry discrepancy">
              <div className="error-header">
                <span className="error-icon">ℹ️</span>
                <span className="error-title">{disc.type?.replace(/_/g, ' ') || 'Discrepancy'}</span>
              </div>
              <div className="error-details">
                {disc.type === 'TIMESTAMP_DRIFT' && (
                  <>
                    <p><strong>Gemini Time:</strong> {disc.gemini_time}</p>
                    <p><strong>VI Time:</strong> {disc.vi_time}</p>
                    <p><strong>Drift:</strong> {disc.drift_seconds}s</p>
                    <p><strong>Resolution:</strong> {disc.resolution}</p>
                  </>
                )}
                {disc.type === 'PLATE_MISMATCH' && (
                  <>
                    <p><strong>Gemini Plate:</strong> {disc.gemini_plate}</p>
                    <p><strong>VI Plate:</strong> {disc.vi_plate}</p>
                    <p><strong>Match Score:</strong> {Math.round((disc.levenshtein_score || 0) * 100)}%</p>
                    <p><strong>Resolution:</strong> {disc.resolution}</p>
                  </>
                )}
                {disc.type === 'CONFIDENCE_ADJUSTMENT' && (
                  <>
                    <p><strong>Original Confidence:</strong> {Math.round((disc.original_confidence || 0) * 100)}%</p>
                    <p><strong>Adjusted Confidence:</strong> {Math.round((disc.adjusted_confidence || 0) * 100)}%</p>
                    <p><strong>Reason:</strong> {disc.reason}</p>
                  </>
                )}
                {/* Generic discrepancy display */}
                {!['TIMESTAMP_DRIFT', 'PLATE_MISMATCH', 'CONFIDENCE_ADJUSTMENT'].includes(disc.type) && (
                  <p>{JSON.stringify(disc, null, 2)}</p>
                )}
              </div>
            </div>
          ))}
          
          {/* VI Limitations */}
          {errorLog.vi_limitations && errorLog.vi_limitations.map((limitation, idx) => (
            <div key={`limitation-${idx}`} className="error-entry discrepancy">
              <div className="error-header">
                <span className="error-icon">ℹ️</span>
                <span className="error-title">{limitation.issue?.replace(/_/g, ' ') || 'System Limitation'}</span>
              </div>
              <div className="error-details">
                {limitation.affected_target && (
                  <p><strong>Affected Target:</strong> {limitation.affected_target}</p>
                )}
                {limitation.description && (
                  <p><strong>Description:</strong> {limitation.description}</p>
                )}
                {limitation.impact && (
                  <p><strong>Impact:</strong> {limitation.impact}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ErrorLogPanel;

